/**
 * Estado das telas de e-mail.
 *
 * - Filtros (projetos, domínios, período) no navegador, por usuário — como as telas de ROI.
 * - Preferências no backend (`/preferences`), com cache local e PUT com debounce de 500 ms.
 * - A cotação do dólar vem do envelope da resposta de cada tela; quem carrega os dados a informa
 *   com `setDolar`, e é dela que a formatação em US$ depende.
 */
import {
    createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
    type MutableRefObject, type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { todayISO } from '@/components/v2/format';
import { dropShared, userKey } from '@/components/v2/userStorage';
import { getEmailFilters, getEmailPrefs, putEmailPrefs } from '@/services/api/email-v1';
import type { EmailPeriodParams, FiltersResponse } from '@/types/email-v1';
import type { BlocksReg, Preview } from '../lib/cards';
import { makeFmt, type Currency, type Fmtr } from '../lib/format';
import {
    DEFAULT_PREFS, mergePrefs, toApiPrefs,
    type EmailPrefs, type OverviewPrefs, type ScreenPrefs,
} from '../lib/prefs';
import type { ScreenKey } from '../lib/screens';

const LS_FILTERS = 'emailv1:filters';
const LS_PREFS = 'emailv1:prefs';
const SAVE_DEBOUNCE = 500;

export interface EmailFilters {
    /** Vazio = todos os projetos do acesso ("all"). */
    projectIds: string[];
    domains: string[];
    from: string;
    to: string;
}

interface Ctx {
    filters: EmailFilters;
    setFilters: (patch: Partial<EmailFilters>) => void;
    params: EmailPeriodParams;
    options: FiltersResponse | null;
    optionsLoading: boolean;
    prefs: EmailPrefs;
    setPrefs: (patch: Partial<EmailPrefs>) => void;
    setScreenPrefs: (k: ScreenKey, patch: Partial<ScreenPrefs>) => void;
    setOverviewPrefs: (patch: Partial<OverviewPrefs>) => void;
    resetScreen: (k: ScreenKey) => void;
    resetOverview: () => void;
    setCurrency: (c: Currency) => void;
    fmt: Fmtr;
    setDolar: (d: number | null | undefined) => void;
    /** Gaveta do Personalizar: a aba aberta, ou null. */
    drawer: string | null;
    setDrawer: (tab: string | null) => void;
    /**
     * O que está na tela, para o Personalizar desenhar a pré-visualização com dado real.
     * É uma ref, não estado: quem lê é o modal, que já redesenha a cada mudança de preferência —
     * virar estado só faria a tela inteira renderizar de novo a cada troca de aba.
     */
    preview: MutableRefObject<Preview | null>;
    registerPreview: (p: Preview | null) => void;
    /**
     * Os blocos da tela de lista que está no ar (indicadores, gráfico, funil), para o Personalizar
     * desenhar a pré-visualização deles. Separado de `preview` porque quem registra é outro
     * componente: um só `registerPreview` apagaria o registro do outro.
     */
    blocks: MutableRefObject<BlocksReg | null>;
    registerBlocks: (b: BlocksReg | null) => void;
}

const EmailCtx = createContext<Ctx | null>(null);

export const useEmail = () => {
    const ctx = useContext(EmailCtx);
    if (!ctx) throw new Error('useEmail fora do EmailProvider');
    return ctx;
};

dropShared(LS_FILTERS, LS_PREFS);

const readFilters = (): EmailFilters => {
    const today = todayISO();
    try {
        const raw = JSON.parse(localStorage.getItem(userKey(LS_FILTERS)) || 'null');
        if (raw && typeof raw.from === 'string' && typeof raw.to === 'string') {
            return {
                projectIds: Array.isArray(raw.projectIds) ? raw.projectIds.map(String) : [],
                domains: Array.isArray(raw.domains) ? raw.domains.map(String) : [],
                from: raw.from, to: raw.to,
            };
        }
    } catch { /* sem storage */ }
    return { projectIds: [], domains: [], from: today, to: today };
};

const readPrefs = (): EmailPrefs => {
    try { return mergePrefs(JSON.parse(localStorage.getItem(userKey(LS_PREFS)) || 'null')); } catch { return mergePrefs(null); }
};

export function EmailProvider({ children }: { children: ReactNode }) {
    const [filters, setFiltersState] = useState<EmailFilters>(readFilters);
    const [prefs, setPrefsState] = useState<EmailPrefs>(readPrefs);
    const [dolar, setDolarState] = useState<number | null>(null);
    const [drawer, setDrawer] = useState<string | null>(null);
    /** O PUT só acontece depois que a resposta do GET chegou: senão o padrão local sobrescreve o salvo. */
    const loaded = useRef(false);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const preview = useRef<Preview | null>(null);
    const registerPreview = useCallback((p: Preview | null) => { preview.current = p; }, []);
    const blocks = useRef<BlocksReg | null>(null);
    const registerBlocks = useCallback((b: BlocksReg | null) => { blocks.current = b; }, []);

    const setFilters = useCallback((patch: Partial<EmailFilters>) => {
        setFiltersState((f) => {
            const next = { ...f, ...patch };
            try { localStorage.setItem(userKey(LS_FILTERS), JSON.stringify(next)); } catch { /* sem storage */ }
            return next;
        });
    }, []);

    const projectId = filters.projectIds.length ? filters.projectIds.join(',') : 'all';

    const params = useMemo<EmailPeriodParams>(() => ({
        project_id: projectId,
        from: filters.from,
        to: filters.to,
        ...(filters.domains.length ? { domains: filters.domains.join(',') } : {}),
    }), [projectId, filters.from, filters.to, filters.domains]);

    const optionsQuery = useQuery({
        queryKey: ['email-filters', projectId],
        queryFn: () => getEmailFilters(filters.projectIds.length ? projectId : undefined),
        staleTime: 60 * 1000,
        retry: false,
        placeholderData: (prev) => prev,
    });

    const prefsQuery = useQuery({
        queryKey: ['email-prefs'],
        queryFn: getEmailPrefs,
        staleTime: 5 * 60 * 1000,
        retry: false,
    });

    useEffect(() => {
        if (!prefsQuery.data) return;
        const merged = mergePrefs(prefsQuery.data.prefs);
        setPrefsState(merged);
        try { localStorage.setItem(userKey(LS_PREFS), JSON.stringify(merged)); } catch { /* sem storage */ }
        loaded.current = true;
    }, [prefsQuery.data]);

    useEffect(() => {
        // sem /preferences (403, API fora) a tela segue com o padrão local, sem tentar salvar
        if (prefsQuery.isError) loaded.current = false;
    }, [prefsQuery.isError]);

    const commit = useCallback((next: EmailPrefs) => {
        setPrefsState(next);
        try { localStorage.setItem(userKey(LS_PREFS), JSON.stringify(next)); } catch { /* sem storage */ }
        if (!loaded.current) return;
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            void putEmailPrefs(toApiPrefs(next)).catch(() => { /* preferência não salva não derruba a tela */ });
        }, SAVE_DEBOUNCE);
    }, []);

    useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

    const setPrefs = useCallback((patch: Partial<EmailPrefs>) => {
        setPrefsState((p) => { const next = { ...p, ...patch }; commit(next); return next; });
    }, [commit]);

    const setScreenPrefs = useCallback((k: ScreenKey, patch: Partial<ScreenPrefs>) => {
        setPrefsState((p) => {
            const next = { ...p, screens: { ...p.screens, [k]: { ...p.screens[k], ...patch } } };
            commit(next);
            return next;
        });
    }, [commit]);

    const setOverviewPrefs = useCallback((patch: Partial<OverviewPrefs>) => {
        setPrefsState((p) => { const next = { ...p, overview: { ...p.overview, ...patch } }; commit(next); return next; });
    }, [commit]);

    const resetScreen = useCallback((k: ScreenKey) => {
        setPrefsState((p) => {
            const next = { ...p, screens: { ...p.screens, [k]: { ...DEFAULT_PREFS.screens[k] } } };
            commit(next);
            return next;
        });
    }, [commit]);

    const resetOverview = useCallback(() => {
        setPrefsState((p) => { const next = { ...p, overview: { ...DEFAULT_PREFS.overview } }; commit(next); return next; });
    }, [commit]);

    const setCurrency = useCallback((c: Currency) => setPrefs({ currency: c }), [setPrefs]);

    const setDolar = useCallback((d: number | null | undefined) => {
        setDolarState((cur) => (typeof d === 'number' && Number.isFinite(d) ? d : cur));
    }, []);

    const fmt = useMemo(() => makeFmt(prefs.currency, dolar), [prefs.currency, dolar]);

    const value = useMemo<Ctx>(() => ({
        filters, setFilters, params,
        options: optionsQuery.data ?? null,
        optionsLoading: optionsQuery.isLoading,
        prefs, setPrefs, setScreenPrefs, setOverviewPrefs, resetScreen, resetOverview,
        setCurrency, fmt, setDolar, drawer, setDrawer, preview, registerPreview, blocks, registerBlocks,
    }), [filters, setFilters, params, optionsQuery.data, optionsQuery.isLoading, prefs, setPrefs,
        setScreenPrefs, setOverviewPrefs, resetScreen, resetOverview, setCurrency, fmt, drawer, registerPreview, registerBlocks]);

    return <EmailCtx.Provider value={value}>{children}</EmailCtx.Provider>;
}
